import type { Locale } from "../../i18n";
import { termsRefusalFrom, type TermsRefusal } from "../booking/operations";

export { termsRefusalFrom, type TermsRefusal };

/**
 * LURA'S TERMS OF SERVICE, AS BACKEND SERVES THEM (booking-v1 1.13+, client-v1 1.2+).
 *
 * The contract, stated once so every caller reads it the same way:
 *
 * - `currentTerms(locale)` is NULL only when no Terms are published. Then no
 *   checkbox is shown and `termsAcceptance` is omitted (a value sent is
 *   ignored).
 * - Once Terms are published, `createBooking` and `account.create` REQUIRE an
 *   acceptance that echoes, EXACTLY, the `currentTerms` answer the person was
 *   shown: `policyId`, `version`, `contentHash` and `locale`. The locale is the
 *   one the ANSWER names, which can differ from the one asked for: a locale
 *   with no published text is answered in English, and saying so is the
 *   point of the field.
 * - INVALID_INPUT `TERMS_ACCEPTANCE_REQUIRED` (nothing sent) and `TERMS_STALE`
 *   (not the Terms in force): re-read, show, untick, ask again. Never resend
 *   on the person's behalf.
 * - SERVICE_NOT_CONFIGURED (503) `TERMS_UNAVAILABLE`: the record cannot be
 *   read right now. Say so, and let the person load the terms again.
 *
 * Web echoes and never decides. Backend records the accepted version and time.
 */

export type TermsLocale = "EN" | "ES";

export interface CurrentTerms {
  readonly policyId: string;
  readonly version: number;
  readonly contentHash: string;
  readonly locale: TermsLocale;
  readonly documentUrl: string;
}

/** Exactly the four fields `TermsAcceptanceInput` takes. Nothing else rides along. */
export interface TermsAcceptance {
  readonly policyId: string;
  readonly version: number;
  readonly contentHash: string;
  readonly locale: TermsLocale;
}

/**
 * WHAT THE PERSON DECIDED, for one send.
 *
 * `acceptance: null` is a decision too: no Terms were published when the page
 * read them, so nothing is sent. A caller holding no decision at all (still
 * loading, unreadable, or published and unticked) holds `null` instead of a
 * `TermsDecision`, and the type keeps it from sending.
 */
export interface TermsDecision {
  readonly acceptance: TermsAcceptance | null;
}

export const NO_TERMS_PUBLISHED: TermsDecision = Object.freeze({ acceptance: null });

/** The UI language, as the `TermsLocale` enum the API takes. */
export function termsLocaleFor(locale: Locale): TermsLocale {
  return locale === "es" ? "ES" : "EN";
}

export type ParsedTerms =
  | { readonly kind: "none" }
  | { readonly kind: "published"; readonly terms: CurrentTerms }
  /** The answer broke the contract. Nothing can be echoed from it, so the page fails closed. */
  | { readonly kind: "malformed" };

const SHA256_HEX = /^[0-9a-f]{64}$/;

/**
 * THE BOUNDARY. Every `currentTerms` answer passes through here before
 * anything renders it or echoes it.
 *
 * Strict on purpose. An echo built from a value the contract does not allow
 * is refused as stale, so the person would tick, confirm and be asked again
 * forever; a `documentUrl` that is not https would put an unvetted target
 * behind the word "Terms". Backend applies the same rules when it publishes
 * (termsOfService.ts: SHA-256 hex, https, integer version >= 1).
 *
 * `undefined` is malformed, not "none": the field is selected, so its absence
 * is a broken response rather than the documented null.
 */
export function parseCurrentTerms(raw: unknown): ParsedTerms {
  if (raw === null) return { kind: "none" };
  if (!raw || typeof raw !== "object") return { kind: "malformed" };
  const r = raw as Record<string, unknown>;
  const { policyId, version, contentHash, locale, documentUrl } = r;
  if (typeof policyId !== "string" || policyId.length === 0) return { kind: "malformed" };
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1) return { kind: "malformed" };
  if (typeof contentHash !== "string" || !SHA256_HEX.test(contentHash)) return { kind: "malformed" };
  if (locale !== "EN" && locale !== "ES") return { kind: "malformed" };
  const href = termsDocumentHref(documentUrl);
  if (href === null) return { kind: "malformed" };
  return {
    kind: "published",
    terms: Object.freeze({ policyId, version, contentHash, locale, documentUrl: href }),
  };
}

/**
 * Hosts a Terms document may live on. Backend publishes every `documentUrl` as
 * exactly `https://uselura.com/terms/<YYYY-MM-DD>/`
 * (lura-service docs/business-logic/terms-of-service-acceptance.md), so
 * uselura.com alone; `www.uselura.com` is not used and not allowed.
 */
const TERMS_HOSTS: ReadonlySet<string> = new Set(["uselura.com"]);

/**
 * THE LINK BEHIND "TERMS OF SERVICE", or null (security audit WEB-TOS-02).
 *
 * https only, on an allowlisted host, with no username or password, and
 * written exactly: no surrounding whitespace and no backslash, both of which
 * a URL parser silently repairs into something else. What is rendered is the
 * parsed `href`, never the raw string. A null makes the whole answer
 * malformed, which the page shows as unreadable (frame 4.5) rather than as
 * "no Terms".
 */
export function termsDocumentHref(raw: unknown): string | null {
  if (typeof raw !== "string" || raw !== raw.trim() || raw.includes("\\")) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username !== "" || url.password !== "" || url.port !== "") return null;
  if (!TERMS_HOSTS.has(url.hostname)) return null;
  return url.href;
}

/** The echo: the four fields, copied, frozen. `documentUrl` is not part of it. */
export function acceptanceOf(terms: CurrentTerms): TermsAcceptance {
  return Object.freeze({
    policyId: terms.policyId,
    version: terms.version,
    contentHash: terms.contentHash,
    locale: terms.locale,
  });
}

/** Are these the same Terms? Field by field; `documentUrl` is presentation, not identity. */
export function sameTerms(a: TermsAcceptance | null, b: TermsAcceptance | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.policyId === b.policyId &&
    a.version === b.version &&
    a.contentHash === b.contentHash &&
    a.locale === b.locale
  );
}

/** Where the footer's Terms link goes when the page has no `currentTerms` answer. */
export const FALLBACK_TERMS_URL = "https://uselura.com/terms/";
export const PRIVACY_POLICY_URL = "https://uselura.com/privacy/";
